import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { LabelBox, DEFAULT_TEMPLATE, normTemplate, templateSize, addShelfLife } from '../components/LabelDesign';
import printLabels, { printTestLabel } from '../utils/printLabels';
import { apiError, catColor, day } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { packsOf } from '../utils/packs';
import { useCategories } from '../context/CategoriesContext';
import { useDataTable, TableToolbar, TableFilterRow, TablePager } from '../components/DataTable';

export const PRODUCT_TYPES = ['finished', 'raw'];
// Sold by the kilo or by the piece. A KGS product can be weighed out in
// fractions at the till; a PCS one is counted.
export const UNITS = ['KGS', 'PCS'];

const emptyProduct = {
  name: '', code: '', category: 'SWEETS', gstRate: 5, hsnCode: '', shelfLife: '', description: '', isActive: true,
  productType: 'finished', source: 'made', sellMode: 'packs', printName: '', multipleBatch: false, department: '', subCategory: '',
  brand: '', subBrand: '', unit: 'KGS', netWeightUnit: '',
  salesTaxInclusive: true, purchaseGstRate: 5, purchaseTaxInclusive: true, cessRate: 0,
  sellingPrice: 0, mrp: 0, purchasePrice: 0,
  ingredients: '', shortDescription: '', additionalInfo: '', imageUrl: '', nutrition: [],
  variants: [],
};
const emptyVariant = () => ({ sku: '', weightLabel: '', weightGrams: 0, sellingPrice: 0, mrp: 0, purchasePrice: 0, isSellable: true });
const asRows = (v) => { if (Array.isArray(v)) return v; if (typeof v === 'string') { try { return JSON.parse(v) || []; } catch { return []; } } return []; };

export const ProductModal = ({ product, onClose, onSaved }) => {
  // Packs that were removed but had stock history: the row survives so the
  // ledger still reads, and it is neither editable nor sellable.
  const retired = (product?.variants || []).filter((v) => v.isSellable === false);
  const [form, setForm] = useState(
    product
      ? { ...emptyProduct, ...product, nutrition: asRows(product.nutrition), variants: (product.variants || []).filter((v) => v.isSellable !== false) }
      : { ...emptyProduct, variants: [emptyVariant()] }
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('Details');
  const { categories } = useCategories();
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setVar = (i, k, v) => setForm((p) => ({ ...p, variants: p.variants.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)) }));
  const addVar = () => setForm((p) => ({ ...p, variants: [...p.variants, emptyVariant()] }));
  const rmVar = (i) => setForm((p) => ({ ...p, variants: p.variants.filter((_, idx) => idx !== i) }));
  const setNut = (i, k, v) => setForm((p) => ({ ...p, nutrition: p.nutrition.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)) }));
  const addNut = () => setForm((p) => ({ ...p, nutrition: [...p.nutrition, { name: '', value: '' }] }));
  const rmNut = (i) => setForm((p) => ({ ...p, nutrition: p.nutrition.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    if (!form.name || !form.category) { setTab('Details'); setError('Name and category are required'); setBusy(false); return; }
    try {
      const payload = {
        ...form,
        gstRate: Number(form.gstRate) || 0,
        purchaseGstRate: Number(form.purchaseGstRate) || 0,
        cessRate: Number(form.cessRate) || 0,
        nutrition: (form.nutrition || []).filter((n) => n.name),
        variants: form.variants.filter((v) => v.weightLabel).map((v) => ({
          ...v, weightGrams: Number(v.weightGrams) || 0,
          sellingPrice: Number(v.sellingPrice) || 0, mrp: Number(v.mrp) || Number(v.sellingPrice) || 0,
        })),
      };
      const { data } = product ? await api.put(`/products/${product.id}`, payload) : await api.post('/products', payload);
      toast.success(product ? 'Product updated' : 'Product created');
      onSaved(data.product); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={product ? 'Edit Product' : 'Add Product'} onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</button></>}>
      <form onSubmit={save}>
        <div className="tabs" style={{ marginBottom: '.8rem' }}>
          <button type="button" className={`tab${tab === 'Details' ? ' active' : ''}`} onClick={() => setTab('Details')}>Details</button>
          {form.sellMode !== 'loose' && <button type="button" className={`tab${tab === 'Variants' ? ' active' : ''}`} onClick={() => setTab('Variants')}>Variants ({form.variants.length})</button>}
        </div>
        {tab === 'Details' && (<>
        {/* Identity */}
        <div className="row">
          <div className="field" style={{ flex: 2 }}><label>Name *</label><input value={form.name} onChange={(e) => set('name', e.target.value)} required /></div>
          <div className="field"><label>Product code</label><input value={form.code} onChange={(e) => set('code', e.target.value)} placeholder="Your own code" /></div>
          <div className="field"><label>Print name</label><input value={form.printName} onChange={(e) => set('printName', e.target.value)} placeholder="On labels/bills" /></div>
          <div className="field"><label>Product type</label><select value={form.productType} onChange={(e) => set('productType', e.target.value)}>{PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
          <div className="field"><label>Source</label><select value={form.source} onChange={(e) => set('source', e.target.value)}><option value="made">Made in-house (Kitchen)</option><option value="bought">Bought (Vendor)</option></select></div>
          {/* Loose = sold and requested by weight only; it drops the packs so the price per unit shows below. */}
          <div className="field"><label>Sold as</label><select value={form.sellMode || 'packs'} onChange={(e) => setForm((p) => ({ ...p, sellMode: e.target.value, variants: e.target.value === 'loose' ? [] : p.variants }))}><option value="packs">Packs</option><option value="loose">Loose (by {form.unit || 'kg'})</option></select></div>
        </div>
        <div className="row">
          <div className="field"><label>Brand</label><input value={form.brand} onChange={(e) => set('brand', e.target.value)} /></div>
          <div className="field"><label>Sub brand</label><input value={form.subBrand} onChange={(e) => set('subBrand', e.target.value)} /></div>
          <div className="field"><label>Image URL</label><input value={form.imageUrl} onChange={(e) => set('imageUrl', e.target.value)} placeholder="/images/…" /></div>
        </div>

        {/* Classification */}
        <div className="row">
          <div className="field"><label>Department</label><input value={form.department} onChange={(e) => set('department', e.target.value)} /></div>
          <div className="field"><label>Category *</label>
            <select value={form.category || ''} onChange={(e) => set('category', e.target.value)} required>
              <option value="">— select category —</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              {form.category && !categories.includes(form.category) && <option value={form.category}>{form.category}</option>}
            </select>
          </div>
          <div className="field"><label>Sub category</label><input value={form.subCategory} onChange={(e) => set('subCategory', e.target.value)} /></div>
        </div>
        <div className="row">
          <div className="field"><label>Unit</label><select value={form.unit} onChange={(e) => set('unit', e.target.value)}>
              {(UNITS.includes(form.unit) ? UNITS : [form.unit, ...UNITS]).map((u) => <option key={u} value={u}>{u}</option>)}
            </select></div>
          {form.sellMode !== 'loose' && <div className="field"><label>Net weight unit</label><input value={form.netWeightUnit} onChange={(e) => set('netWeightUnit', e.target.value)} placeholder="e.g. 500 GMS" /></div>}
          <div className="field"><label>Shelf life</label><input value={form.shelfLife} onChange={(e) => set('shelfLife', e.target.value)} placeholder="6 months" /></div>
          <div className="field" style={{ flex: '0 0 auto', alignSelf: 'end' }}><label className="chk"><input type="checkbox" checked={!!form.multipleBatch} onChange={(e) => set('multipleBatch', e.target.checked)} /> Multiple batch</label></div>
        </div>

        {/* Tax */}
        <div className="row">
          <div className="field"><label>Sales GST %</label><input type="number" step="0.01" value={form.gstRate} onChange={(e) => set('gstRate', e.target.value)} /></div>
          <div className="field" style={{ flex: '0 0 auto', alignSelf: 'end' }}><label className="chk"><input type="checkbox" checked={!!form.salesTaxInclusive} onChange={(e) => set('salesTaxInclusive', e.target.checked)} /> Sales tax incl.</label></div>
          <div className="field"><label>Purchase GST %</label><input type="number" step="0.01" value={form.purchaseGstRate} onChange={(e) => set('purchaseGstRate', e.target.value)} /></div>
          <div className="field" style={{ flex: '0 0 auto', alignSelf: 'end' }}><label className="chk"><input type="checkbox" checked={!!form.purchaseTaxInclusive} onChange={(e) => set('purchaseTaxInclusive', e.target.checked)} /> Purchase tax incl.</label></div>
        </div>
        <div className="row">
          <div className="field"><label>Cess %</label><input type="number" step="0.01" value={form.cessRate} onChange={(e) => set('cessRate', e.target.value)} /></div>
          <div className="field"><label>HSN code</label><input value={form.hsnCode} onChange={(e) => set('hsnCode', e.target.value)} /></div>
          <div className="field" style={{ flex: '0 0 auto', alignSelf: 'end' }}><label className="chk"><input type="checkbox" checked={!!form.isActive} onChange={(e) => set('isActive', e.target.checked)} /> Active</label></div>
        </div>
        {form.variants.length === 0 && (
          <div className="row">
            <div className="field"><label>Selling price ₹ <span className="muted small">(per {form.unit || 'unit'}, incl GST)</span></label><input type="number" step="0.01" value={form.sellingPrice} onChange={(e) => set('sellingPrice', e.target.value)} /></div>
            <div className="field"><label>MRP ₹</label><input type="number" step="0.01" value={form.mrp} onChange={(e) => set('mrp', e.target.value)} /></div>
            {form.source === 'bought' && <div className="field"><label>Purchase ₹ <span className="muted small">(vendor cost)</span></label><input type="number" step="0.01" value={form.purchasePrice} onChange={(e) => set('purchasePrice', e.target.value)} /></div>}
          </div>
        )}

        {/* Content */}
        <div className="row">
          <div className="field" style={{ flex: 1 }}><label>Short description</label><input value={form.shortDescription} onChange={(e) => set('shortDescription', e.target.value)} /></div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}><label>Description</label><textarea rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
        </div>
        <div className="row">
          <div className="field" style={{ flex: 1 }}><label>Additional info</label><textarea rows={2} value={form.additionalInfo} onChange={(e) => set('additionalInfo', e.target.value)} /></div>
        </div>

        {/* Nutrition */}
        <div className="section-head" style={{ marginTop: '.5rem' }}><h3 style={{ fontSize: '.95rem' }}>Nutrition</h3><button type="button" className="btn btn-outline btn-sm" onClick={addNut}>+ Add row</button></div>
        {(form.nutrition || []).length > 0 && (
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>Nutrition name</th><th>Value</th><th></th></tr></thead>
              <tbody>
                {form.nutrition.map((n, i) => (
                  <tr key={i}>
                    <td><input value={n.name} onChange={(e) => setNut(i, 'name', e.target.value)} placeholder="Energy" /></td>
                    <td><input value={n.value} onChange={(e) => setNut(i, 'value', e.target.value)} placeholder="250 kcal" /></td>
                    <td><button type="button" className="btn btn-danger btn-sm" onClick={() => rmNut(i)}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        </>)}
        {tab === 'Variants' && form.sellMode !== 'loose' && (<>
        {/* Variants */}
        <div className="section-head" style={{ marginTop: '.5rem' }}><h3 style={{ fontSize: '.95rem' }}>Variants (packs) <span className="muted small" style={{ fontWeight: 400 }}>— optional; leave empty for by-weight items like sweets (sold in kg/L)</span></h3><button type="button" className="btn btn-outline btn-sm" onClick={addVar}>+ Add variant</button></div>
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>SKU</th><th>Label *</th><th>Grams</th><th>Selling ₹ (MRP incl GST)</th><th>MRP</th><th>Purchase ₹ <span className="muted small">(bought)</span></th><th title="Off sale hides the pack from the till and from labels, keeping its history">Sellable</th><th></th></tr></thead>
            <tbody>
              {form.variants.length === 0 && <tr><td colSpan={8} className="empty">No variants — this product is sold by weight (kg/L).</td></tr>}
              {form.variants.map((v, i) => (
                <tr key={i}>
                  <td><input value={v.sku || ''} onChange={(e) => setVar(i, 'sku', e.target.value)} placeholder="PICK-MANGO-500G" /></td>
                  <td><input value={v.weightLabel} onChange={(e) => setVar(i, 'weightLabel', e.target.value)} placeholder="500g" /></td>
                  <td><input type="number" style={{ width: 80 }} value={v.weightGrams} onChange={(e) => setVar(i, 'weightGrams', e.target.value)} /></td>
                  <td><input type="number" step="0.01" value={v.sellingPrice} onChange={(e) => setVar(i, 'sellingPrice', e.target.value)} /></td>
                  <td><input type="number" step="0.01" value={v.mrp} onChange={(e) => setVar(i, 'mrp', e.target.value)} /></td>
                  <td><input type="number" step="0.01" style={{ width: 90 }} value={v.purchasePrice || 0} onChange={(e) => setVar(i, 'purchasePrice', e.target.value)} /></td>
                  <td><input type="checkbox" checked={v.isSellable !== false} onChange={(e) => setVar(i, 'isSellable', e.target.checked)} /></td>
                  <td><button type="button" className="btn btn-danger btn-sm" onClick={() => rmVar(i)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {retired.filter((v) => !form.variants.some((f) => f.id === v.id)).length > 0 && (
          <p className="muted small" style={{ marginTop: '.5rem' }}>
            Off sale — kept because stock has moved against {retired.length > 1 ? 'them' : 'it'}:{' '}
            {retired.filter((v) => !form.variants.some((f) => f.id === v.id)).map((v) => (
              <button key={v.id} type="button" className="btn btn-outline btn-sm" style={{ marginRight: '.4rem' }}
                onClick={() => setForm((f) => ({ ...f, variants: [...f.variants, { ...v, isSellable: true }] }))}>
                {v.weightLabel} — restore
              </button>
            ))}
          </p>
        )}
        </>)}
        {error && <p className="form-error">{error}</p>}
      </form>
    </Modal>
  );
};

// The barcode-label DESIGN (size, layout, fields, border) lives in Settings and
// is saved as the `labelTemplate` setting. This print dialog loads that finalized
// design and only collects run-time inputs: manufacturing date + copies per pack.
export const LabelsModal = ({ product, onClose }) => {
  const variants = (product.variants || []).filter((v) => v.isSellable !== false);
  const [mfgDate, setMfgDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  // Per-pack copy counts — 0 = don't print this pack. Defaults to 1 each.
  const [copies, setCopies] = useState(() => Object.fromEntries(variants.map((v) => [v.id, 1])));

  useEffect(() => {
    api.get('/settings').then(({ data }) => { if (data.settings?.labelTemplate) setTemplate(normTemplate(data.settings.labelTemplate)); }).catch(() => {});
  }, []);

  const t = normTemplate(template);
  const { W, H } = templateSize(t);
  const bestBefore = addShelfLife(mfgDate, product.shelfLife);
  const setCopy = (id, n) => setCopies((c) => ({ ...c, [id]: Math.max(0, Number(n) || 0) }));
  const setAll = (n) => setCopies(Object.fromEntries(variants.map((v) => [v.id, Math.max(0, Number(n) || 0)])));

  const labels = [];
  for (const v of variants) for (let i = 0; i < (copies[v.id] || 0); i++) labels.push(v);
  const preview = variants.filter((v) => (copies[v.id] || 0) > 0);

  return (
    <Modal title={`Barcode labels — ${product.name}`} onClose={onClose} wide
      footer={<><span className="muted small" style={{ marginRight: 'auto' }}>{labels.length} label{labels.length === 1 ? '' : 's'} · {W}×{H} mm</span><button className="btn btn-outline" onClick={() => printTestLabel({ template: t })} title="One framed label, to check the page against the sticker">Test print</button>
      <button className="btn btn-primary" onClick={() => printLabels({ template: t, product, labels, mfgDate })} disabled={!labels.length}>Print</button></>}>
      <div className="no-print" style={{ marginBottom: '.9rem' }}>
        <div className="flex gap" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ maxWidth: 160 }}><label>Manufacturing date</label><input type="date" value={mfgDate} onChange={(e) => setMfgDate(e.target.value)} /></div>
        </div>
        {variants.length > 0 && (
          <div className="flex gap" style={{ alignItems: 'center', flexWrap: 'wrap', gap: '.4rem', marginTop: '.7rem' }}>
            <span className="muted small">Set every pack to:</span>
            {[10, 25, 50, 100].map((n) => <button key={n} type="button" className="btn btn-outline btn-sm" onClick={() => setAll(n)}>{n}</button>)}
            <input type="number" min="0" placeholder="qty…" onChange={(e) => setAll(e.target.value)} style={{ width: 76 }} title="Set all packs to this many copies" />
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setAll(0)}>Clear</button>
          </div>
        )}
        <div className="flex gap" style={{ flexWrap: 'wrap', marginTop: '.6rem', gap: '.5rem .9rem', alignItems: 'center' }}>
          <span className="muted small">Copies per pack:</span>
          {variants.map((v) => (
            <span key={v.id} className="flex gap" style={{ gap: '.35rem', alignItems: 'center' }}>
              <span className="chip-static">{v.weightLabel}</span>
              <input type="number" min="0" value={copies[v.id] ?? 0} onChange={(e) => setCopy(v.id, e.target.value)} style={{ width: 76 }} />
            </span>
          ))}
          {!variants.length && <span className="muted small">This product has no variants to label.</span>}
        </div>
        <p className="muted small" style={{ margin: '.6rem 0 0' }}>Design: {W}×{H} mm · {t.layout === 'roll' ? 'label roll' : 'A4 sheet'} · shelf life {product.shelfLife || 'not set'}{bestBefore ? ` · best before ${day(bestBefore)}` : ''}. <Link to="/settings" onClick={onClose}>Change label design</Link></p>
        {t.layout === 'roll' && <p className="muted small" style={{ margin: '.3rem 0 0' }}>Roll mode: pick your TSC TE244 as the printer and set its paper to <strong>{W} × {H} mm</strong> — one label prints per feed.</p>}
      </div>
      {/* On-screen preview only — one sample per pack, so a 500-label run does not
          render 500 barcodes twice. The printer gets the full run from the portal. */}
      <div className={`label-sheet ${t.layout} no-print`} style={{ display: 'flex', flexWrap: 'wrap', gap: '3mm' }}>
        {preview.map((v, i) => <LabelBox key={i} template={t} product={product} variant={v} mfgDate={mfgDate} />)}
        {!labels.length && <p className="muted">Set copies above to preview and print labels.</p>}
      </div>
      {labels.length > preview.length && (
        <p className="muted small no-print" style={{ marginTop: '.5rem' }}>
          Preview shows one label per pack — printing sends all {labels.length}.
        </p>
      )}
    </Modal>
  );
};

// ── Download / upload products + variants ──────────────────────
const EXPORT_COLS = [
  ['ProductId', 'productId'], ['Product Code', 'code'], ['Product Name', 'name'], ['Category', 'category'], ['Brand', 'brand'],
  ['GST %', 'gstRate'], ['Source', 'source'], ['Unit', 'unit'], ['Sold As', 'sellMode'], ['Shelf Life', 'shelfLife'], ['Active', 'active'],
  ['VariantId', 'variantId'], ['Pack', 'weightLabel'], ['Weight (g)', 'weightGrams'],
  ['MRP', 'mrp'], ['Selling Price', 'sellingPrice'], ['SKU', 'sku'], ['Barcode', 'barcode'],
];
const HEADER_MAP = {
  productid: 'productId', id: 'productId',
  productcode: 'code', code: 'code', itemcode: 'code',
  productname: 'name', name: 'name',
  category: 'category', brand: 'brand',
  gst: 'gstRate', gstrate: 'gstRate',
  source: 'source', shelflife: 'shelfLife',
  unit: 'unit', soldas: 'sellMode', sellmode: 'sellMode', sellas: 'sellMode',
  active: 'isActive', status: 'isActive', isactive: 'isActive',
  variantid: 'variantId',
  pack: 'weightLabel', packsize: 'weightLabel', weightlabel: 'weightLabel', size: 'weightLabel',
  weightg: 'weightGrams', weightgrams: 'weightGrams', grams: 'weightGrams', weight: 'weightGrams',
  mrp: 'mrp', sellingprice: 'sellingPrice', price: 'sellingPrice',
  sku: 'sku', barcode: 'barcode',
};
const normHeader = (h) => String(h || '').toLowerCase().replace(/[^a-z0-9]/g, '');
function parseCSV(text) {
  const rows = []; let row = []; let field = ''; let i = 0; let q = false;
  while (i < text.length) {
    const c = text[i];
    if (q) { if (c === '"') { if (text[i + 1] === '"') { field += '"'; i += 2; continue; } q = false; i++; continue; } field += c; i++; continue; }
    if (c === '"') { q = true; i++; continue; }
    if (c === ',') { row.push(field); field = ''; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += c; i++;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}
const csvCell = (v) => { const s = v == null ? '' : String(v); return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
const exportProductsCSV = (products) => {
  const lines = [EXPORT_COLS.map(([h]) => h).join(',')];
  const cell = (p, v, key) => {
    if (key === 'productId') return csvCell(p.id);
    if (key === 'active') return csvCell(p.isActive ? 'Active' : 'Inactive');
    if (key === 'sellMode') return csvCell(p.sellMode === 'loose' ? 'Loose' : 'Packs');
    // A row with no pack carries the product's own price per unit (kg/L).
    if (!v && (key === 'mrp' || key === 'sellingPrice')) return csvCell(p[key] ?? '');
    if (['variantId', 'weightLabel', 'weightGrams', 'mrp', 'sellingPrice', 'sku', 'barcode'].includes(key)) return csvCell(v ? (key === 'variantId' ? v.id : v[key] ?? '') : '');
    return csvCell(p[key] ?? '');
  };
  for (const p of products) {
    const vs = packsOf(p);   // off-sale packs and loose items' packs aren't exported
    if (vs.length) for (const v of vs) lines.push(EXPORT_COLS.map(([, k]) => cell(p, v, k)).join(','));
    else lines.push(EXPORT_COLS.map(([, k]) => cell(p, null, k)).join(','));
  }
  const blob = new Blob(['﻿' + lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `products-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

const ProductsImportModal = ({ onClose, onDone }) => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const onFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    setFileName(f.name); setError(''); setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const grid = parseCSV(String(reader.result));
        if (grid.length < 2) { setError('File has no data rows.'); setRows([]); return; }
        const headers = grid[0].map((h) => HEADER_MAP[normHeader(h)] || null);
        if (!headers.includes('name') && !headers.includes('productId')) { setError('Need a Product Name or ProductId column.'); setRows([]); return; }
        const out = [];
        for (let r = 1; r < grid.length; r++) {
          const cells = grid[r];
          if (!cells.length || cells.every((c) => !String(c).trim())) continue;
          const obj = {};
          headers.forEach((field, idx) => { if (field) obj[field] = String(cells[idx] || '').trim(); });
          if (!obj.name && !obj.productId) continue;
          out.push(obj);
        }
        setRows(out);
        if (!out.length) setError('No data rows found.');
      } catch (err) { setError('Could not parse file: ' + err.message); setRows([]); }
    };
    reader.readAsText(f);
  };

  const submit = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const { data } = await api.post('/products/import', { rows });
      setResult(data.result);
      toast.success(`Imported — ${data.result.productsCreated} new products, ${data.result.variantsCreated} new variants`);
      onDone && onDone();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Upload products" onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Close</button><button className="btn btn-primary" onClick={submit} disabled={busy || !rows.length}>{busy ? 'Uploading…' : `Upload ${rows.length} rows`}</button></>}>
      <p className="muted small" style={{ marginTop: 0 }}>Upload a <strong>CSV</strong> (one row per pack/variant). Columns: ProductId, Product Code, Product Name, Category, Brand, GST %, Source, Shelf Life, Active, VariantId, Pack, Weight (g), MRP, Selling Price, SKU, Barcode. Products match by <strong>ProductId → Name</strong>; variants by <strong>VariantId → Barcode → SKU → Pack</strong>, and <strong>new variants are created</strong>. Only the columns present are updated.</p>
      <div className="field"><label>CSV file</label><input type="file" accept=".csv,text/csv" onChange={onFile} /></div>
      {fileName && <p className="muted small">{fileName} — <strong>{rows.length}</strong> rows.</p>}
      {rows.length > 0 && (
        <div className="table-wrap" style={{ maxHeight: 220, overflow: 'auto' }}>
          <table className="grid">
            <thead><tr><th>Product</th><th>Category</th><th>Pack</th><th className="right">Price</th><th>Barcode</th></tr></thead>
            <tbody>
              {rows.slice(0, 10).map((r, i) => <tr key={i}><td>{r.name}</td><td className="muted">{r.category}</td><td>{r.weightLabel}</td><td className="right">{r.sellingPrice}</td><td className="mono muted">{r.barcode}</td></tr>)}
              {rows.length > 10 && <tr><td colSpan={5} className="muted">…and {rows.length - 10} more</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {result && <p className="mt"><strong>Done.</strong> Products: {result.productsCreated} new, {result.productsUpdated} updated · Variants: {result.variantsCreated} new, {result.variantsUpdated} updated{result.skipped ? `, ${result.skipped} skipped` : ''}{result.errors?.length ? `, ${result.errors.length} errors` : ''}.</p>}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// Owner tool: bulk-add weight packs (e.g. 250/500 GMS) to a category (pickles).
const BulkPacksModal = ({ onClose, onSaved }) => {
  const [category, setCategory] = useState('pickle');
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setRows(null);
    api.get(`/products/add-packs/preview?category=${encodeURIComponent(category)}&weights=250,500`)
      .then(({ data }) => { setRows(data.candidates || []); setSel(Object.fromEntries((data.candidates || []).map((c) => [c.productId, true]))); })
      .catch((e) => { toast.error(apiError(e)); setRows([]); });
  }, [category]);
  useEffect(() => { load(); }, [load]);

  const list = rows || [];
  const selected = list.filter((c) => sel[c.productId]);
  const allOn = list.length > 0 && selected.length === list.length;
  const toggleAll = () => setSel(allOn ? {} : Object.fromEntries(list.map((c) => [c.productId, true])));
  const totalPacks = selected.reduce((n, c) => n + c.toAdd.length, 0);
  const gLabel = (g) => (g >= 1000 ? `${g / 1000}KG` : `${g}g`);

  const apply = async () => {
    if (!selected.length) return toast.error('Select at least one product');
    if (!window.confirm(`Add ${totalPacks} pack(s) across ${selected.length} product(s)?`)) return;
    setBusy(true);
    try { const { data } = await api.post('/products/add-packs', { productIds: selected.map((c) => c.productId), weightsG: [250, 500] }); toast.success(data.message || 'Packs added'); onSaved && onSaved(); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Add weight packs (250 / 500 GMS)" onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Close</button>
        <button className="btn btn-primary" onClick={apply} disabled={busy || !selected.length}>{busy ? 'Adding…' : `Add ${totalPacks || 0} pack${totalPacks === 1 ? '' : 's'}`}</button></>}>
      <div className="flex gap" style={{ alignItems: 'flex-end', marginBottom: '.4rem' }}>
        <div className="field" style={{ maxWidth: 240, marginBottom: 0 }}><label>Category contains</label><input value={category} onChange={(e) => setCategory(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') load(); }} placeholder="pickle" /></div>
        <button className="btn btn-outline btn-sm" onClick={load}>Find</button>
      </div>
      <p className="muted small" style={{ marginTop: 0 }}>Adds <strong>250 GMS</strong> and <strong>500 GMS</strong> packs to matching products (skips any that already have them). Pack price is derived from the product’s per-unit price — adjust later if needed.</p>
      {rows === null ? <div className="spinner" /> : !list.length ? <p className="muted">No matching products need these packs.</p> : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr>
              <th style={{ width: 32 }}><input type="checkbox" checked={allOn} onChange={toggleAll} title="Select all" /></th>
              <th>Product</th><th>Category</th><th>Existing packs</th><th>Will add</th>
            </tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.productId}>
                  <td><input type="checkbox" checked={!!sel[c.productId]} onChange={(e) => setSel((s) => ({ ...s, [c.productId]: e.target.checked }))} /></td>
                  <td>{c.name}{c.code ? <span className="muted small"> · {c.code}</span> : ''}</td>
                  <td>{c.category ? <span className="cat-chip" style={{ '--cat': catColor(c.category) }}>{c.category}</span> : '—'}</td>
                  <td className="muted small">{c.existing.length ? c.existing.map(gLabel).join(', ') : '—'}</td>
                  <td>{c.toAdd.map((g) => <span key={g} className="badge badge-green" style={{ marginRight: 4 }}>{g} GMS</span>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

const Products = () => {
  const { can } = useAuth();
  const isOwner = can('*');   // recipes (ingredient formulas) are shown only to the owner
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);   // undefined=closed, null=new
  const [importing, setImporting] = useState(false);
  const [bulkPacks, setBulkPacks] = useState(false);
  const [recipes, setRecipes] = useState({});      // productId -> recipe (with ingredients)
  const [openRecipe, setOpenRecipe] = useState(null);

  const load = () => { setLoading(true); api.get('/products?all=1').then(({ data }) => setProducts(data.products || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); };
  useEffect(load, []);

  // Owner only: load recipes and index them by product so each product row can
  // reveal its ingredient formula.
  useEffect(() => {
    if (!isOwner) return;
    api.get('/recipes?all=1').then(({ data }) => {
      const map = {};
      for (const r of (data.recipes || [])) if (!map[r.productId]) map[r.productId] = r;
      setRecipes(map);
    }).catch(() => {});
  }, [isOwner]);

  const onSaved = (p) => setProducts((prev) => { const i = prev.findIndex((x) => x.id === p.id); return i >= 0 ? prev.map((x) => (x.id === p.id ? p : x)) : [...prev, p]; });

  const dt = useDataTable(products, {
    searchPlaceholder: 'Search name, code, ID, brand, barcode…',
    searchKeys: ['id', 'code', 'name', 'category', 'brand', (p) => (p.variants || []).map((v) => v.barcode).join(' ')],
    columns: [
      { key: 'code', label: 'Code', value: (p2) => p2.code, placeholder: 'Code' },
      { key: 'name', label: 'Product', value: (p2) => p2.name, placeholder: 'Name' },
      { key: 'category', label: 'Category', value: (p2) => p2.category, type: 'select' },
      { key: 'brand', label: 'Brand', value: (p2) => p2.brand, type: 'select' },
      { key: 'unit', label: 'Unit', value: (p2) => p2.unit || 'unit', type: 'select' },
    ],
    filters: [
      { key: 'category', label: 'Category', value: (p) => p.category },
      { key: 'status', label: 'Status', value: (p) => (p.isActive ? 'Active' : 'Inactive') },
    ],
  });

  return (
    <div>
      <TableToolbar dt={dt} right={<>
        <button className="btn btn-outline" onClick={() => exportProductsCSV(products)} disabled={!products.length} title="Download all products & variants as CSV to edit and re-upload">⭳ Download CSV</button>
        {can('products:write') && <button className="btn btn-outline" onClick={() => setImporting(true)}>⭱ Upload CSV</button>}
        {isOwner && <button className="btn btn-outline" onClick={() => setBulkPacks(true)} title="Bulk-add 250/500 GMS packs to a category (e.g. pickles)">＋ Bulk packs</button>}
        {can('products:write') && <button className="btn btn-primary" onClick={() => setEditing(null)}>+ Add Product</button>}
      </>} />
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr><th className="right">ID</th><th>Code</th><th>Name</th><th>Category</th><th>Brand</th><th className="right">GST%</th><th>Unit</th><th>Variants</th>{isOwner && <th>Recipe</th>}<th>Status</th><th></th></tr>
              <TableFilterRow dt={dt} cells={[null, 'code', 'name', 'category', 'brand', null, 'unit', null, ...(isOwner ? [null] : []), null, null]} />
            </thead>
            <tbody>
              {dt.rows.map((p) => {
                const r = recipes[p.id];
                const expanded = openRecipe === p.id;
                return (
                <React.Fragment key={p.id}>
                <tr>
                  <td className="right mono muted">{p.id}</td>
                  <td className="mono">{p.code || <span className="muted">—</span>}</td>
                  <td><Link to={`/products/${p.id}`} className="link-strong">{p.name}</Link></td>
                  <td>{p.category && <span className="cat-chip" style={{ '--cat': catColor(p.category) }}>{p.category}</span>}</td>
                  <td>{p.brand || '—'}</td>
                  <td className="right">{Number(p.gstRate)}%</td>
                  <td className="muted">{p.unit || 'unit'}</td>
                  <td>{(p.variants || []).map((v) => v.weightLabel).join(', ') || '—'}</td>
                  {isOwner && <td>{r
                    ? <button className="btn btn-outline btn-sm" onClick={() => setOpenRecipe(expanded ? null : p.id)}>{expanded ? 'Hide' : 'Recipe'}</button>
                    : <span className="muted">—</span>}</td>}
                  <td>{p.isActive ? <span className="badge badge-green">Active</span> : <span className="badge badge-gray">Inactive</span>}</td>
                  <td className="right"><Link to={`/products/${p.id}`} className="muted" title="Open">›</Link></td>
                </tr>
                {isOwner && expanded && r && (
                  <tr>
                    <td colSpan={11} style={{ background: 'var(--ivory, #faf6ed)' }}>
                      <div style={{ padding: '.4rem .25rem' }}>
                        <strong>{r.name}</strong>
                        <span className="muted small"> · yields {Number(r.baseYieldQty)} {r.baseYieldUnit}{r.costPerKg ? ` · cost ₹${r.costPerKg}/kg` : ''}</span>
                        <table className="grid" style={{ marginTop: '.4rem', maxWidth: 480 }}>
                          <thead><tr><th>Ingredient</th><th className="right">Quantity</th></tr></thead>
                          <tbody>
                            {(r.ingredients || []).map((ing) => (
                              <tr key={ing.id}><td>{ing.rawMaterial?.name || `Material #${ing.rawMaterialId}`}</td><td className="right">{Number(ing.quantity)} {ing.unit}</td></tr>
                            ))}
                            {!(r.ingredients || []).length && <tr><td colSpan={2} className="muted">No ingredients defined.</td></tr>}
                          </tbody>
                        </table>
                        <Link to={`/recipes/${r.id}`} className="link-strong" style={{ display: 'inline-block', marginTop: '.4rem' }}>Open full recipe ›</Link>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
                );
              })}
              {dt.rows.length === 0 && <tr><td colSpan={isOwner ? 11 : 10} className="empty">{dt.isFiltered ? 'No products match your search.' : 'No products yet.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {!loading && <TablePager dt={dt} />}
      {editing !== undefined && <ProductModal product={editing} onClose={() => setEditing(undefined)} onSaved={onSaved} />}
      {importing && <ProductsImportModal onClose={() => setImporting(false)} onDone={load} />}
      {bulkPacks && <BulkPacksModal onClose={() => setBulkPacks(false)} onSaved={load} />}
    </div>
  );
};

export default Products;
